import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Box, HStack } from "../../components/utils";
import { useAuthStore } from "../../store";
import { RoundHeaderCards } from "./RoundHeaderCard";
import BCHeader from "../../shared/BCHeader";
import { useGetBulkConversions } from "../../queries/transactionRound";
import { useQuickRoundTransactionTypeStore } from "../../store/quickRoundTransactionType";
import { IndividualRoundDetailModel } from "../../types/RoundsModel";
import { AgGridTransactionsCaptable } from "../../types/AllTransactionModel";
import { useInternalSwitchStateStore } from "../../store/searchDropDown";
import { SwitchDropDownModel } from "./RoundsAllTransactions";
import SearchAutoCompleteDropDown from "../../components/shared/SearchAutoCompleteDropDown";
import { useInvGetInstrumentAndShareHolder } from "../../queries/investorPortal";
import { useGetInstrumentAndShareHolder } from "../../queries/quickRound";
import { useShareholderInstrumentStore } from "../../store/shareholderInstrumentStore";
import AGGridRoundsTable from "./RoundsAGTable";
import { handleEventForTracking } from "../../amplitudeAnalytics";
import { bulkConversionHeaderCardList } from "../../constants/TransactionConstants";
import { InternalValuationAction } from "../../components/InternalValuationAction";
import ConversionTableGrid, {
  ConversionDto,
} from "../newQuickTransaction/ConversionAgTable";

const transformBulkConversionsToDto = (
  capTableData: any[]
): ConversionDto[] => {
  if (!capTableData || capTableData.length === 0) {
    return [];
  }

  return capTableData.map((item: any, index: number) => {
    const transformedItem = {
      investorId: item.investorId,
      investorName: undefined,
      sourceInstrumentClassId: item.sourceInstrumentClassId,
      sourceNoOfShares: item.sourceNoOfShares,
      conversionSecurityId: item.finalInstrumentClassId,
      noOfSharesAfterConversion: item.finalNoOfShares,
      date: item.date,
      conversionDate: item.date,
      conversionPPS: undefined,
      investmentRecordId: item.investmentRecordId,
    };

    return transformedItem;
  });
};

export default function RoundsConversion() {
  const { id, cId, token } = useParams();
  const eventId = id || "";
  const companyId = useAuthStore.getState().companyId || "";
  const { data: bulkConversionsData, isLoading: isLoadingBulkConversions } =
    useGetBulkConversions(companyId, eventId);
  const navigate = useNavigate();
  const transactionSwitchStore = useInternalSwitchStateStore();
  const [selectedTransaction, setSelectedTransaction] =
    useState<SwitchDropDownModel>({ name: "", id: "", type: "" });

  const [conversionData, setConversionData] = useState<ConversionDto[]>([]);

  function getSelectedValue(transaction?: SwitchDropDownModel) {
    if (transaction) {
      if (cId) {
        handleEventForTracking({ eventName: "Inv Transactions Search" });
        navigate(
          `/inv/transactions/${transaction.type?.toLowerCase()}/${
            transaction.id
          }/${cId}/${token}`
        );
      } else {
        handleEventForTracking({ eventName: "Transactions Search" });
        navigate(
          `/transactions/${transaction.type?.toLowerCase()}/${transaction.id}`
        );
      }
    }
  }

  const {
    refetch: getInstrumentAndShareHolder,
    data: instrumentShareholderInfoData,
  } = cId
    ? useInvGetInstrumentAndShareHolder(cId, token || "")
    : useGetInstrumentAndShareHolder(companyId, cId);

  const shareholderAndInstrumentstore = useShareholderInstrumentStore();

  useEffect(() => {
    if (
      cId ||
      (shareholderAndInstrumentstore.shareholders.length === 0 &&
        shareholderAndInstrumentstore.instrumentClasses.length === 0)
    )
      getInstrumentAndShareHolder().then((data) => {
        shareholderAndInstrumentstore.setInstrumentClass(
          data?.data?.instrumentClasses || []
        );
        shareholderAndInstrumentstore.setShareholders(
          data?.data?.shareholders || []
        );
        shareholderAndInstrumentstore.setInstrumentClassSubTypes(
          data?.data?.instrumentClassSubTypes || {}
        );
      });
  }, []);

  useEffect(() => {
    if (bulkConversionsData) {
      setSelectedTransaction({
        name: bulkConversionsData.name || "conversion",
        id: bulkConversionsData.id,
        type: "conversion",
      });
    }
  }, [bulkConversionsData]);

  // Transform transactions data to ConversionDto format
  const transformedConversionData = transformBulkConversionsToDto(
    bulkConversionsData?.transactions || []
  );

  const _captableData: AgGridTransactionsCaptable[] =
    bulkConversionsData?.capTable || [];
  const handleDataChange = (data: ConversionDto[]) => {
    setConversionData(data);
  };

  return (
    <div
      className={`${cId ? "bg-white px-4" : ""} ${
        isLoadingBulkConversions ? "loading" : "min-h-full"
      }`}
    >
      <HStack className="justify-between min-h-[45px] items-center">
        <BCHeader
          className="items-baseline "
          bcTitle="Transaction"
          bcSubTitle="conversion"
          cin={cId}
          token={token}
        />
        <HStack>
          <InternalValuationAction isReadOnly={Boolean(cId)} eventId={bulkConversionsData?.id || ""} />
          {selectedTransaction.id ? (
            <Box className="bg-white rounded-md w-48 ml-4 h-8">
              <SearchAutoCompleteDropDown
                placeholder="Switch Transaction"
                data={
                  transactionSwitchStore.transaction as SwitchDropDownModel[]
                }
                getSelectedValue={getSelectedValue}
                selectedOption={selectedTransaction as SwitchDropDownModel}
                field={"name" as keyof SwitchDropDownModel}
              />
            </Box>
          ) : null}
        </HStack>
      </HStack>

      <div className="mx-auto max-w-full pb-8">
        {bulkConversionsData && (
          <dl className="flex flex-row gap-8 pb-8 mt-5 overflow-x-scroll scrollbar-hide">
            {bulkConversionHeaderCardList.map((headertype) => (
              <RoundHeaderCards
                key={headertype}
                roundDetail={
                  bulkConversionsData as unknown as IndividualRoundDetailModel
                }
                headerType={headertype}
              />
            ))}
          </dl>
        )}
      </div>

      {transformedConversionData.length > 0 && (
        <div className="mb-8">
          <ConversionTableGrid
            previewData={transformedConversionData}
            onDataChange={handleDataChange}
            isEditable={false}
            isShareholder= {!!cId}
          />
        </div>
      )}
      <AGGridRoundsTable
        capTableData={_captableData as unknown as AgGridTransactionsCaptable[]}
        isInvestorPortal={!!cId}
        roundName={bulkConversionsData?.name || ""}
        eventId={eventId}
        mode={"conversion"}
      />
    </div>
  );
}
